"use client";
import { FC, HTMLProps, forwardRef } from "react";

// libs
import classNames from "classnames";

// components
import { Error } from "@/components/common";

// assets
import {
  PrimaryChecked,
  PrimaryUnchecked,
  SquareChecked,
  SquareUnchecked,
} from "@/components/assets/icons";

type CheckboxProps = {
  id?: string;
  label?: string;
  name: string;
  error?: string | boolean;
  value?: string | number;
  type?: string;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  infoContent?: any;
  iconPosition?: "left" | "right";
  secondary?: boolean;
  additionalLabel?: string;
} & HTMLProps<HTMLInputElement>;

const Checkbox: FC<CheckboxProps> = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      label = "",
      value,
      type = "text",
      name,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      icon,
      infoContent,
      iconPosition = "right",
      secondary,
      additionalLabel,
      width = "w-full",
      ...props
    },
    ref,
  ) => {
    const inputClasses = classNames(
      "w-[25px]",
      "h-[25px]",
      "px-8",
      "border",
      "text-base",
      "focus:outline-none",
      className,
      {
        // error
        "border-red-600": !!error,
        // active
        "border-transparent active:border-[#cfe0e2]": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
      },
    );

    return (
      <div className={classNames("relative", containerClassName, width)}>
        {!secondary ? (
          <div
            className={classNames(
              "input-wrapper relative w-full bg-transparent py-[1px]",
              wrapperClassName,
            )}
          >
            <label
              className={classNames(
                "flex items-center justify-start gap-[6px] text-xs font-bold text-black",
                {
                  "cursor-pointer": !disabled,
                  "cursor-not-allowed opacity-50": disabled,
                },
                labelClassName,
              )}
              htmlFor={id}
            >
              {icon && (
                <div className="check-box-icon-wrapper primary-color secondary-bg mr-[8px] h-[38px] min-h-[38px] w-[38px] min-w-[38px] rounded-[8px] p-[6px]">
                  {icon}
                </div>
              )}
              <div>
                <span
                  className="block text-xs font-bold text-black"
                  dangerouslySetInnerHTML={{ __html: label }}
                />
                {additionalLabel && (
                  <span
                    className="mt-[4px] text-sm font-normal text-black opacity-60"
                    dangerouslySetInnerHTML={{ __html: additionalLabel }}
                  />
                )}
              </div>
              <div
                className={classNames("relative flex", {
                  "ml-auto": !!label,
                })}
              >
                <input
                  id={id}
                  ref={ref}
                  className={classNames("invisible pt-4", inputClasses)}
                  name={name}
                  disabled={disabled}
                  maxLength={maxLength}
                  type="checkbox"
                  placeholder=""
                  value={value || ""}
                  checked={props.checked}
                  {...props}
                />
                {props.checked ? (
                  <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
                    <PrimaryChecked />
                  </div>
                ) : (
                  <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
                    <PrimaryUnchecked />
                  </div>
                )}
              </div>
            </label>
          </div>
        ) : (
          <div
            className={classNames(
              "input-wrapper relative w-full",
              wrapperClassName,
            )}
          >
            <label
              className={classNames(
                "flex items-center gap-[5px] text-base font-normal text-black",
                {
                  "cursor-pointer": !disabled,
                  "cursor-not-allowed opacity-50": disabled,
                },
                labelClassName,
              )}
              htmlFor={id}
            >
              <div className="relative flex">
                <input
                  id={id}
                  ref={ref}
                  className={classNames("pt-4", inputClasses)}
                  name={name}
                  disabled={disabled}
                  maxLength={maxLength}
                  type="checkbox"
                  placeholder=""
                  value={value || ""}
                  checked={props.checked}
                  {...props}
                />
                {props.checked ? (
                  <div className="absolute left-0 top-0 h-full w-full bg-white">
                    <SquareChecked />
                  </div>
                ) : (
                  <div className="absolute left-0 top-0 h-full w-full bg-white">
                    <SquareUnchecked />
                  </div>
                )}
              </div>
              <span dangerouslySetInnerHTML={{ __html: label }} />
            </label>
          </div>
        )}
        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
